// The record page's History section (MOTHER-83): GET /api/objects/:object/:id/history
// (apps/server/src/recordHistory.mjs, MOTHER-82), the wording of each entry,
// and how the section pages through them. Free of React and auth, so it is unit
// tested (historyApi.test.ts).
import type { ObjectKey } from '../navigation';

/** How the section reaches the API: authenticatedFetch in the app, a fake in tests. */
export type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

/** One change to a tracked field, as the route answers it. */
export interface HistoryEntry {
  field: string;
  /** The field's label as the record page shows it. */
  label: string;
  /** The stored text before the change; null when it was empty or the record was being created. */
  oldValue: string | null;
  /** The stored text after the change; null when it was emptied. */
  newValue: string | null;
  /** ISO timestamp, to the microsecond. */
  changedAt: string;
  /** The person's name or email; null for a change made outside the app. */
  changedBy: string | null;
}

/** One page of a record's changes, newest first. */
export interface HistoryPage {
  entries: HistoryEntry[];
  /** Whether older entries exist than the last one on this page. */
  hasMore: boolean;
}

/** Entries shown to start, and added by each "Show more". */
export const HISTORY_STEP = 10;

/** What a failed load says, under the section's heading. */
export const HISTORY_ERROR = 'History could not be loaded.';

/** How an empty value is written. */
export const EMPTY_VALUE = '(empty)';

/** Who made a change made outside the app. */
export const UNKNOWN_PERSON = 'Someone';

/** GET /api/objects/:object/:id/history, the page older than `before` when given. */
export function historyUrl(object: ObjectKey, id: string, before?: string): string {
  const url = `/api/objects/${encodeURIComponent(object)}/${encodeURIComponent(id)}/history`;
  return before === undefined ? url : `${url}?${new URLSearchParams({ before }).toString()}`;
}

/** A load that failed: a 5xx, a refusal other than 404, a network error or a body that isn't a page. */
export class HistoryLoadError extends Error {
  constructor(readonly status: number | null) {
    super(HISTORY_ERROR);
  }
}

function isEntry(value: unknown): value is HistoryEntry {
  if (typeof value !== 'object' || value === null) return false;
  const e = value as Record<string, unknown>;
  const text = (v: unknown) => typeof v === 'string';
  const textOrNull = (v: unknown) => v === null || typeof v === 'string';
  return text(e.field) && text(e.label) && text(e.changedAt) && textOrNull(e.oldValue) && textOrNull(e.newValue) && textOrNull(e.changedBy);
}

/**
 * One page of the record's history, or null when the route answers 404 (the
 * object keeps no history, or the record is gone): the section then shows
 * nothing. Anything else that isn't a page throws HistoryLoadError.
 */
export async function fetchHistory(
  fetcher: Fetch,
  object: ObjectKey,
  id: string,
  options: { before?: string; signal?: AbortSignal } = {},
): Promise<HistoryPage | null> {
  let response: Response;
  try {
    response = await fetcher(historyUrl(object, id, options.before), options.signal ? { signal: options.signal } : {});
  } catch (error) {
    if (options.signal?.aborted) throw error;
    throw new HistoryLoadError(null);
  }
  if (response.status === 404) return null;
  if (!response.ok) throw new HistoryLoadError(response.status);
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new HistoryLoadError(response.status);
  }
  const page = body as Partial<HistoryPage> | null;
  if (!page || !Array.isArray(page.entries) || !page.entries.every(isEntry) || typeof page.hasMore !== 'boolean') {
    throw new HistoryLoadError(response.status);
  }
  return { entries: page.entries, hasMore: page.hasMore };
}

/** An entry's words: who, what they did to which field, and the values. */
export interface HistoryWords {
  actor: string;
  /** "set" when there was no old value (the record was created, or the field was empty). */
  verb: 'changed' | 'set';
  label: string;
  /** The old value, written out; absent for "set". */
  from?: string;
  to: string;
}

const written = (value: string | null): string => (value === null || value === '' ? EMPTY_VALUE : value);

/** The parts of "<Name> changed <Field Label> from <old> to <new>" or "<Name> set <Field Label> to <new>". */
export function historyWords(entry: HistoryEntry): HistoryWords {
  const actor = entry.changedBy?.trim() ? entry.changedBy.trim() : UNKNOWN_PERSON;
  const to = written(entry.newValue);
  if (entry.oldValue === null || entry.oldValue === '') return { actor, verb: 'set', label: entry.label, to };
  return { actor, verb: 'changed', label: entry.label, from: written(entry.oldValue), to };
}

/** An entry as one sentence of plain text. */
export function historySentence(entry: HistoryEntry): string {
  const w = historyWords(entry);
  return w.from === undefined ? `${w.actor} set ${w.label} to ${w.to}` : `${w.actor} changed ${w.label} from ${w.from} to ${w.to}`;
}

/** Identifies an entry across pages (the route answers no id). */
export function entryKey(entry: HistoryEntry): string {
  return JSON.stringify([entry.changedAt, entry.field, entry.oldValue, entry.newValue, entry.changedBy]);
}

/** The loaded entries with a next page appended, leaving out any already there. */
export function appendPage(entries: HistoryEntry[], page: HistoryEntry[]): HistoryEntry[] {
  const seen = new Set(entries.map(entryKey));
  const added = page.filter((entry) => {
    const key = entryKey(entry);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return added.length ? [...entries, ...added] : entries;
}

/**
 * What "Show more" does next. The route answers up to 50 entries a page and
 * the section shows 10 at a time, so the next 10 may already be loaded
 * (`reveal`); otherwise, while the route says older entries exist, it loads
 * the page before the last loaded entry (`fetch`). `none`: nothing more, so no
 * button.
 */
export function nextStep(loaded: number, shown: number, hasMore: boolean): 'reveal' | 'fetch' | 'none' {
  if (loaded >= shown + HISTORY_STEP) return 'reveal';
  if (hasMore) return 'fetch';
  return loaded > shown ? 'reveal' : 'none';
}

/** A value with a run of more than this many characters breaks anywhere, so it wraps at 390 px. */
export const LONG_TOKEN = 24;

/** Whether a value holds a word too long to wrap at a space (a URL, an id). */
export function hasLongToken(value: string): boolean {
  return value.split(/\s+/).some((word) => word.length > LONG_TOKEN);
}

/**
 * What the section shows. `loading` and `hidden` (a 404, or no entries) show
 * nothing; `error` is the first page failing.
 */
export type RecordHistory =
  | { state: 'loading' }
  | { state: 'hidden' }
  | { state: 'error' }
  | { state: 'ready'; entries: HistoryEntry[]; hasMore: boolean; shown: number; moreBusy: boolean; moreFailed: boolean };

/** Whether the section shows at all: not while loading, for a 404 or with no entries. */
export function historyShown(history: RecordHistory): boolean {
  return history.state === 'ready' || history.state === 'error';
}
